"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.canonicalizeMetricStringValue = exports.isSparseCoverageMetric = exports.isAuthorableRealtimeMetric = exports.findRealtimeMetric = exports.realtimeMetricCatalog = exports.RealtimeMetricDefinition = exports.realtimeGroupEventSignalMetrics = exports.realtimeGroupParentMetrics = exports.realtimeGroupMetrics = exports.realtimeDomainMetadataMetrics = exports.realtimeDomainAnalyticsMetrics = exports.realtimeDomainToolMetrics = exports.realtimeGooglePlacesCurrentMetrics = exports.realtimeLinkedInEmployeeGrowthMetrics = exports.realtimeLinkedInEmployeeGrowthPeriods = exports.realtimeLinkedInCurrentMetrics = exports.realtimeApolloHeadcountGrowthMetrics = exports.realtimeApolloHeadcountHistoricalMetrics = exports.realtimeApolloHeadcountHistoricalPeriods = exports.realtimeApolloHeadcountCurrentMetrics = exports.realtimeApolloOrganizationMetrics = exports.realtimePrvTrademarkMetrics = exports.realtimeBuzzEventMetrics = exports.workforceEmployeesCurrentFactPolicy = exports.RealtimeWorkforceEvidence = exports.RealtimeWorkforceFactPolicy = exports.MetricCoverage = exports.RealtimeMetricEvidencePolicy = exports.toPublicRealtimeMetricEvidence = exports.RealtimePublicMetricEvidence = exports.RealtimePublicDerivedMetricEvidence = exports.RealtimePublicFxConversionEvidence = exports.RealtimePublicRawMetricEvidence = exports.RealtimeMetricEvidence = exports.RealtimeDerivedMetricEvidence = exports.RealtimeRawMetricEvidence = exports.RealtimeMetricTemporalSemantics = exports.RealtimeMetricSourceSystem = exports.MetricDataScope = exports.MetricTextSearchPolicy = exports.MetricCurrencyPolicy = exports.MetricAvailability = exports.MetricQueryPhase = void 0;
const effect_1 = require("effect");
const model_1 = require("./model");
/** Where the metric is evaluated in the candidate-selection pipeline. */
exports.MetricQueryPhase = effect_1.Schema.Literals(['eligibility', 'scoring']);
/**
 * Publication lifecycle for a metric. Only authorable metrics may be used in
 * a saved definition; planned and deferred metrics remain visible to migration
 * tooling without becoming an accidental legacy-view fallback.
 */
exports.MetricAvailability = effect_1.Schema.Literals(['authorable', 'planned', 'deferred']);
exports.MetricCurrencyPolicy = effect_1.Schema.Literal('same_currency_only');
/** Restricts an authorable text predicate to a literal search the catalog can index. */
exports.MetricTextSearchPolicy = effect_1.Schema.Literal('literal_trigram');
/** Whether a metric can be evaluated for every filter or only a client-owned one. */
exports.MetricDataScope = effect_1.Schema.Literal('client');
/** The raw system whose record supplies a realtime metric fact. */
exports.RealtimeMetricSourceSystem = effect_1.Schema.Literals([
    'company_master',
    'valu8_companies',
    'valu8_company_employees',
    'valu8_company_financials',
    'annual_report',
    'buzz',
    'company_workplace',
    'jobtech_active_ads',
    'career_hiring',
    'domains',
    'domain_orgnr_map',
    'pipedrive',
    'ecb',
    'prv',
    'apollo',
    'linkedin',
    'google_places',
    'valu8_group_structure',
]);
/**
 * The date semantics carried by a fact in addition to its source observation.
 *
 * `observedAt` is always required on the fact itself. This field tells callers
 * whether the metric also represents a reported period, a published event, or
 * a value derived from other source facts.
 */
exports.RealtimeMetricTemporalSemantics = effect_1.Schema.Literals([
    'current_observation',
    'historical_observation',
    'published_event',
    'reported_period',
    'derived',
]);
/**
 * Internal source-backed evidence for one live metric value.
 *
 * A missing fact is represented by a null metric value, never by fabricated
 * provenance. Derived metrics retain their own calculation evidence and name
 * every input source that contributed to the result.
 *
 * @example
 * new RealtimeRawMetricEvidence({
 *   kind: 'raw',
 *   sourceSystem: 'annual_report',
 *   observedAt: '2026-08-14T09:00:00.000Z',
 *   fiscalYear: 2025,
 * })
 */
class RealtimeRawMetricEvidence extends effect_1.Schema.Class('RealtimeRawMetricEvidence')({
    kind: effect_1.Schema.Literal('raw'),
    sourceSystem: exports.RealtimeMetricSourceSystem.pipe(effect_1.Schema.annotate({ description: 'Raw system that supplied the metric fact.' })),
    observedAt: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'ISO-8601 timestamp when Bloodline observed or refreshed the source fact.' })),
    sourceRecordId: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'Stable raw-source record identifier for the fact.' })),
    effectiveAt: effect_1.Schema.optional(effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'ISO-8601 timestamp when the fact became effective.' }))),
    fiscalYear: effect_1.Schema.optional(effect_1.Schema.Int.pipe(effect_1.Schema.check(effect_1.Schema.isBetween({ minimum: 1900, maximum: 2100 })), effect_1.Schema.annotate({ description: 'Reported fiscal year for period-backed facts.' }))),
    derivedFrom: effect_1.Schema.optional(effect_1.Schema.Array(exports.RealtimeMetricSourceSystem).pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'Input source systems for a derived value.' }))),
}) {
}
exports.RealtimeRawMetricEvidence = RealtimeRawMetricEvidence;
/**
 * Evidence for a code-owned calculation. Each input remains tied to the exact
 * raw source record that contributed to the live result; a missing input never
 * receives fabricated provenance.
 */
class RealtimeDerivedMetricEvidence extends effect_1.Schema.Class('RealtimeDerivedMetricEvidence')({
    kind: effect_1.Schema.Literal('derived'),
    formulaKey: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    formulaVersion: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    inputs: effect_1.Schema.Record(effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))), effect_1.Schema.NullOr(RealtimeRawMetricEvidence)),
    fxConversions: effect_1.Schema.optional(effect_1.Schema.Array(effect_1.Schema.Struct({
        provider: effect_1.Schema.Literal('ecb'),
        sourceCurrency: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^[A-Z]{3}$/u))),
        targetCurrency: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^[A-Z]{3}$/u))),
        periodEndDate: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/u))),
        sourceRate: effect_1.Schema.Number.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThan(0))),
        sourceRateDate: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/u))),
        sourceRateSourceRecordId: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
        sourceRateImportedAt: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
        targetRate: effect_1.Schema.Number.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThan(0))),
        targetRateDate: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/u))),
        targetRateSourceRecordId: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
        targetRateImportedAt: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    })).pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)))),
}) {
}
exports.RealtimeDerivedMetricEvidence = RealtimeDerivedMetricEvidence;
/** The provenance shape returned for raw facts and code-owned calculations. */
exports.RealtimeMetricEvidence = effect_1.Schema.Union([RealtimeRawMetricEvidence, RealtimeDerivedMetricEvidence]);
/**
 * `Schema.Class` whose encoded struct also carries the identifier, so OpenAPI keeps the v3 component
 * name instead of `${identifier}Encoded` (see `OpenApiClass` in `@bloodline/api-definition`).
 */
const OpenApiClass = 
// Self and Fields flow into the returned class type, as in `Schema.Class` itself.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
(identifier) => 
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
(fields) => effect_1.Schema.Class(identifier)(effect_1.Schema.Struct(fields).annotate({ identifier }));
/**
 * Public provenance for one raw fact returned in a scoring result.
 *
 * Stable source record identifiers remain an internal debugging and data-quality
 * concern. Result consumers receive the supplying system and the fact's
 * observation and reporting dates instead.
 *
 * @example
 * new RealtimePublicRawMetricEvidence({
 *   kind: 'raw',
 *   sourceSystem: 'annual_report',
 *   observedAt: '2026-08-14T09:00:00.000Z',
 *   fiscalYear: 2025,
 * })
 */
class RealtimePublicRawMetricEvidence extends OpenApiClass('RealtimePublicRawMetricEvidence')({
    kind: effect_1.Schema.Literal('raw'),
    sourceSystem: exports.RealtimeMetricSourceSystem.pipe(effect_1.Schema.annotate({ description: 'Raw system that supplied the metric fact.' })),
    observedAt: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'ISO-8601 timestamp when Bloodline observed or refreshed the source fact.' })),
    effectiveAt: effect_1.Schema.optional(effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'ISO-8601 timestamp when the fact became effective.' }))),
    fiscalYear: effect_1.Schema.optional(effect_1.Schema.Int.pipe(effect_1.Schema.check(effect_1.Schema.isBetween({ minimum: 1900, maximum: 2100 })), effect_1.Schema.annotate({ description: 'Reported fiscal year for period-backed facts.' }))),
    derivedFrom: effect_1.Schema.optional(effect_1.Schema.Array(exports.RealtimeMetricSourceSystem).pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.annotate({ description: 'Input source systems for a derived value.' }))),
}) {
}
exports.RealtimePublicRawMetricEvidence = RealtimePublicRawMetricEvidence;
/**
 * Public ECB FX evidence used by a derived scoring result.
 *
 * @example
 * new RealtimePublicFxConversionEvidence({
 *   provider: 'ecb',
 *   sourceCurrency: 'EUR',
 *   targetCurrency: 'SEK',
 *   periodEndDate: '2025-12-31',
 *   sourceRate: 1,
 *   sourceRateDate: '2025-12-31',
 *   sourceRateImportedAt: '2026-01-01T00:00:00.000Z',
 *   targetRate: 11.2,
 *   targetRateDate: '2025-12-31',
 *   targetRateImportedAt: '2026-01-01T00:00:00.000Z',
 * })
 */
class RealtimePublicFxConversionEvidence extends OpenApiClass('RealtimePublicFxConversionEvidence')({
    provider: effect_1.Schema.Literal('ecb'),
    sourceCurrency: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^[A-Z]{3}$/u))),
    targetCurrency: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^[A-Z]{3}$/u))),
    periodEndDate: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/u))),
    sourceRate: effect_1.Schema.Number.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThan(0))),
    sourceRateDate: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/u))),
    sourceRateImportedAt: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    targetRate: effect_1.Schema.Number.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThan(0))),
    targetRateDate: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/u))),
    targetRateImportedAt: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
}) {
}
exports.RealtimePublicFxConversionEvidence = RealtimePublicFxConversionEvidence;
/**
 * Public provenance for a code-owned calculation returned in a scoring result.
 * Its inputs expose source and date evidence, but never internal source record
 * identifiers.
 */
class RealtimePublicDerivedMetricEvidence extends OpenApiClass('RealtimePublicDerivedMetricEvidence')({
    kind: effect_1.Schema.Literal('derived'),
    formulaKey: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    formulaVersion: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    inputs: effect_1.Schema.Record(effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))), effect_1.Schema.NullOr(RealtimePublicRawMetricEvidence)),
    fxConversions: effect_1.Schema.optional(effect_1.Schema.Array(RealtimePublicFxConversionEvidence).pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)))),
}) {
}
exports.RealtimePublicDerivedMetricEvidence = RealtimePublicDerivedMetricEvidence;
/** The result-API-safe projection of realtime metric provenance. */
exports.RealtimePublicMetricEvidence = effect_1.Schema.Union([
    RealtimePublicRawMetricEvidence,
    RealtimePublicDerivedMetricEvidence,
]);
const toPublicRawMetricEvidence = (evidence) => new RealtimePublicRawMetricEvidence({
    kind: evidence.kind,
    sourceSystem: evidence.sourceSystem,
    observedAt: evidence.observedAt,
    ...(evidence.effectiveAt === undefined ? {} : { effectiveAt: evidence.effectiveAt }),
    ...(evidence.fiscalYear === undefined ? {} : { fiscalYear: evidence.fiscalYear }),
    ...(evidence.derivedFrom === undefined ? {} : { derivedFrom: evidence.derivedFrom }),
});
/** Projects internal provenance to the source-and-date evidence safe for result APIs. */
const toPublicRealtimeMetricEvidence = (evidence) => {
    if (evidence.kind === 'raw') {
        return toPublicRawMetricEvidence(evidence);
    }
    return new RealtimePublicDerivedMetricEvidence({
        kind: evidence.kind,
        formulaKey: evidence.formulaKey,
        formulaVersion: evidence.formulaVersion,
        inputs: Object.fromEntries(Object.entries(evidence.inputs).map(([metric, input]) => [
            metric,
            input === null ? null : toPublicRawMetricEvidence(input),
        ])),
        ...(evidence.fxConversions === undefined
            ? {}
            : {
                fxConversions: evidence.fxConversions.map(({ provider, sourceCurrency, targetCurrency, periodEndDate, sourceRate, sourceRateDate, sourceRateImportedAt, targetRate, targetRateDate, targetRateImportedAt, }) => new RealtimePublicFxConversionEvidence({
                    provider,
                    sourceCurrency,
                    targetCurrency,
                    periodEndDate,
                    sourceRate,
                    sourceRateDate,
                    sourceRateImportedAt,
                    targetRate,
                    targetRateDate,
                    targetRateImportedAt,
                })),
            }),
    });
};
exports.toPublicRealtimeMetricEvidence = toPublicRealtimeMetricEvidence;
/**
 * Catalog-level promise that a metric currently emits source-and-time evidence.
 *
 * The field remains optional while the metric rollout is in progress: a metric
 * must not advertise this policy until its compiler projection returns the
 * matching evidence for every result.
 */
class RealtimeMetricEvidencePolicy extends OpenApiClass('RealtimeMetricEvidencePolicy')({
    sourceSystems: effect_1.Schema.Array(exports.RealtimeMetricSourceSystem).pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    temporalSemantics: exports.RealtimeMetricTemporalSemantics,
    requiresObservedAt: effect_1.Schema.Literal(true),
    requiresSourceRecordId: effect_1.Schema.Literal(true),
}) {
}
exports.RealtimeMetricEvidencePolicy = RealtimeMetricEvidencePolicy;
/**
 * How widely the backing source covers the company population.
 *
 * A metric is `sparse` when its source is populated per company by a sync that
 * has not reached most of them. This matters because an absent value is not a
 * failed comparison: the SQL predicate is NULL, so the company is dropped from
 * the candidate set. An eligibility predicate on a sparse metric therefore
 * returns a small or empty page that is indistinguishable from "nothing
 * matched the criteria" unless the caller is told.
 */
exports.MetricCoverage = effect_1.Schema.Literals(['broad', 'sparse']);
/** The provenance and freshness constraints for a raw workforce fact. */
exports.RealtimeWorkforceFactPolicy = effect_1.Schema.Struct({
    source: effect_1.Schema.Literal('valu8_unconsolidated'),
    maxAgeInYears: effect_1.Schema.Int.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThan(0))),
    unavailableHandling: effect_1.Schema.Literal('exclude'),
});
/** Evidence returned with a V1 result when it evaluates workforce facts. */
exports.RealtimeWorkforceEvidence = effect_1.Schema.Struct({
    source: effect_1.Schema.Literal('valu8_unconsolidated'),
    year: effect_1.Schema.Int,
    ageInYears: effect_1.Schema.Int.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThanOrEqualTo(0))),
    maxAgeInYears: effect_1.Schema.Int.pipe(effect_1.Schema.check(effect_1.Schema.isGreaterThan(0))),
});
exports.workforceEmployeesCurrentFactPolicy = {
    source: 'valu8_unconsolidated',
    maxAgeInYears: 2,
    unavailableHandling: 'exclude',
};
const companyMasterEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['company_master'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const valu8CompanyEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_companies'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const workforceEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_company_employees'],
    temporalSemantics: 'reported_period',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const workforceDerivedEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_company_employees'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const revenueDerivedEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_company_financials', 'annual_report', 'valu8_company_employees'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const financialStatementEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_company_financials', 'annual_report'],
    temporalSemantics: 'reported_period',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const financialDerivedEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_company_financials', 'annual_report'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const workplaceDerivedEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_company_employees', 'company_workplace'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const valu8GroupStructureEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_group_structure'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const valu8GroupStructureHistoricalEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_group_structure'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const valu8GroupEventEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['valu8_group_structure'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const buzzSignalEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['buzz'],
    temporalSemantics: 'published_event',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
/** PRV trademark applications are registry events observed by the PRV sync. */
const prvTrademarkEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['prv'],
    temporalSemantics: 'published_event',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
exports.realtimeBuzzEventMetrics = [
    {
        key: 'signal.raised_capital.within_window',
        displayName: 'Capital raising in the last 90 days',
        sourceColumn: 'is_raising_capital',
        legacyMetricKey: 'companyBuzzIsRaisingCapital',
        sourceIndexName: 'buzz_realtime_capital_signal_idx',
    },
    {
        key: 'signal.ipo.within_window',
        displayName: 'IPO activity in the last 90 days',
        sourceColumn: 'is_ipo',
        legacyMetricKey: 'companyBuzzIsIpo',
        sourceIndexName: 'buzz_realtime_ipo_signal_idx',
    },
    {
        key: 'signal.merger.within_window',
        displayName: 'Merger activity in the last 90 days',
        sourceColumn: 'is_merger',
        legacyMetricKey: 'companyBuzzIsMerger',
        sourceIndexName: 'buzz_realtime_merger_signal_idx',
    },
    {
        key: 'signal.moving.within_window',
        displayName: 'Relocation activity in the last 90 days',
        sourceColumn: 'is_moving',
        legacyMetricKey: 'companyBuzzIsMoving',
        sourceIndexName: 'buzz_realtime_moving_signal_idx',
    },
    {
        key: 'signal.hiring.within_window',
        displayName: 'Hiring activity in the last 90 days',
        sourceColumn: 'is_hiring',
        legacyMetricKey: 'companyBuzzIsHiring',
        sourceIndexName: 'buzz_realtime_hiring_signal_idx',
    },
    {
        key: 'signal.firing.within_window',
        displayName: 'Workforce reduction in the last 90 days',
        sourceColumn: 'is_firing',
        legacyMetricKey: 'companyBuzzIsFiring',
        sourceIndexName: 'buzz_realtime_firing_signal_idx',
    },
    {
        key: 'signal.closing.within_window',
        displayName: 'Closing activity in the last 90 days',
        sourceColumn: 'is_closing',
        legacyMetricKey: 'companyBuzzIsClosing',
        sourceIndexName: 'buzz_realtime_closing_signal_idx',
    },
    {
        key: 'signal.opening.within_window',
        displayName: 'New-location opening in the last 90 days',
        sourceColumn: 'is_opening',
        legacyMetricKey: 'companyBuzzIsOpening',
        sourceIndexName: 'buzz_realtime_opening_signal_idx',
    },
    {
        key: 'signal.expanding.within_window',
        displayName: 'Expansion in the last 90 days',
        sourceColumn: 'is_expanding',
        legacyMetricKey: 'companyBuzzIsExpanding',
        sourceIndexName: 'buzz_realtime_expanding_signal_idx',
    },
    {
        key: 'signal.downsizing.within_window',
        displayName: 'Downsizing in the last 90 days',
        sourceColumn: 'is_downsizing',
        legacyMetricKey: 'companyBuzzIsDownsizing',
        sourceIndexName: 'buzz_realtime_downsizing_signal_idx',
    },
    {
        key: 'signal.restructuring.within_window',
        displayName: 'Restructuring in the last 90 days',
        sourceColumn: 'is_restructuring',
        legacyMetricKey: 'companyBuzzIsRestructuring',
        sourceIndexName: 'buzz_realtime_restructuring_signal_idx',
    },
    {
        key: 'signal.new_product_intro.within_window',
        displayName: 'New product introduction in the last 90 days',
        sourceColumn: 'is_new_product_intro',
        legacyMetricKey: 'companyBuzzIsNewProductIntro',
        sourceIndexName: 'buzz_realtime_new_product_intro_signal_idx',
    },
    {
        key: 'signal.has_moved.within_window',
        displayName: 'Confirmed move in the last 90 days',
        sourceColumn: 'has_moved',
        legacyMetricKey: 'companyBuzzHasMoved',
        sourceIndexName: 'buzz_realtime_has_moved_signal_idx',
    },
    {
        key: 'signal.changed_ceo.within_window',
        displayName: 'CEO change in the last 90 days',
        sourceColumn: 'is_changing_ceo',
        legacyMetricKey: 'companyBuzzIsChangingCeo',
        sourceIndexName: 'buzz_realtime_ceo_signal_idx',
    },
    {
        key: 'signal.changed_board.within_window',
        displayName: 'Board change in the last 90 days',
        sourceColumn: 'is_changing_board',
        legacyMetricKey: 'companyBuzzIsChangingBoard',
        sourceIndexName: 'buzz_realtime_board_change_signal_idx',
    },
    {
        key: 'signal.changed_management_team.within_window',
        displayName: 'Management-team change in the last 90 days',
        sourceColumn: 'is_changing_management_team',
        legacyMetricKey: 'companyBuzzIsChangingManagementTeam',
        sourceIndexName: 'buzz_realtime_management_change_signal_idx',
    },
    {
        key: 'signal.growth_geography.within_window',
        displayName: 'Geographic expansion in the last 90 days',
        sourceColumn: 'is_growth_geog',
        legacyMetricKey: 'companyBuzzIsGrowthGeog',
        sourceIndexName: 'buzz_realtime_growth_geography_signal_idx',
    },
    {
        key: 'signal.growth_segment.within_window',
        displayName: 'New market segment in the last 90 days',
        sourceColumn: 'is_growth_seg',
        legacyMetricKey: 'companyBuzzIsGrowthSeg',
        sourceIndexName: 'buzz_realtime_growth_segment_signal_idx',
    },
    {
        key: 'signal.pricing_model.within_window',
        displayName: 'Pricing-model change in the last 90 days',
        sourceColumn: 'is_pricing_model',
        legacyMetricKey: 'companyBuzzIsPricingModel',
        sourceIndexName: 'buzz_realtime_pricing_model_signal_idx',
    },
    {
        key: 'signal.partnership.within_window',
        displayName: 'Partnership activity in the last 90 days',
        sourceColumn: 'is_partnership',
        legacyMetricKey: 'companyBuzzIsPartnership',
        sourceIndexName: 'buzz_realtime_partnership_signal_idx',
    },
    {
        key: 'signal.m_and_a.within_window',
        displayName: 'M&A activity in the last 90 days',
        sourceColumn: 'is_m_and_a',
        legacyMetricKey: 'companyBuzzIsMAndA',
        sourceIndexName: 'buzz_realtime_m_and_a_signal_idx',
    },
    {
        key: 'signal.divestiture.within_window',
        displayName: 'Divestiture activity in the last 90 days',
        sourceColumn: 'is_divest',
        legacyMetricKey: 'companyBuzzIsDivest',
        sourceIndexName: 'buzz_realtime_divestiture_signal_idx',
    },
    {
        key: 'signal.cost_saving.within_window',
        displayName: 'Cost-saving initiative in the last 90 days',
        sourceColumn: 'is_cost_save',
        legacyMetricKey: 'companyBuzzIsCostSave',
        sourceIndexName: 'buzz_realtime_cost_saving_signal_idx',
    },
    {
        key: 'signal.restructuring_initiative.within_window',
        displayName: 'Restructuring initiative in the last 90 days',
        sourceColumn: 'is_restruct',
        legacyMetricKey: 'companyBuzzIsRestruct',
        sourceIndexName: 'buzz_realtime_restructuring_initiative_signal_idx',
    },
    {
        key: 'signal.layoff.within_window',
        displayName: 'Layoff event in the last 90 days',
        sourceColumn: 'is_layoff',
        legacyMetricKey: 'companyBuzzIsLayoff',
        sourceIndexName: 'buzz_realtime_layoff_signal_idx',
    },
    {
        key: 'signal.hiring_plan.within_window',
        displayName: 'Hiring plan in the last 90 days',
        sourceColumn: 'is_hire_plan',
        legacyMetricKey: 'companyBuzzIsHirePlan',
        sourceIndexName: 'buzz_realtime_hiring_plan_signal_idx',
    },
    {
        key: 'signal.leadership.within_window',
        displayName: 'Leadership change in the last 90 days',
        sourceColumn: 'is_leadership',
        legacyMetricKey: 'companyBuzzIsLeadership',
        sourceIndexName: 'buzz_realtime_leadership_signal_idx',
    },
    {
        key: 'signal.capex_scale.within_window',
        displayName: 'Capital-expenditure scale-up in the last 90 days',
        sourceColumn: 'is_capex_scale',
        legacyMetricKey: 'companyBuzzIsCapexScale',
        sourceIndexName: 'buzz_realtime_capex_scale_signal_idx',
    },
    {
        key: 'signal.supply_chain.within_window',
        displayName: 'Supply-chain activity in the last 90 days',
        sourceColumn: 'is_supply_chain',
        legacyMetricKey: 'companyBuzzIsSupplyChain',
        sourceIndexName: 'buzz_realtime_supply_chain_signal_idx',
    },
    {
        key: 'signal.digital_ai.within_window',
        displayName: 'Digital or AI initiative in the last 90 days',
        sourceColumn: 'is_digi_ai',
        legacyMetricKey: 'companyBuzzIsDigiAi',
        sourceIndexName: 'buzz_realtime_digital_ai_signal_idx',
    },
    {
        key: 'signal.esg_target.within_window',
        displayName: 'ESG target update in the last 90 days',
        sourceColumn: 'is_esg_target',
        legacyMetricKey: 'companyBuzzIsEsgTarget',
        sourceIndexName: 'buzz_realtime_esg_target_signal_idx',
    },
    {
        key: 'signal.esg_event.within_window',
        displayName: 'ESG event in the last 90 days',
        sourceColumn: 'is_esg_event',
        legacyMetricKey: 'companyBuzzIsEsgEvent',
        sourceIndexName: 'buzz_realtime_esg_event_signal_idx',
    },
    {
        key: 'signal.regulatory_change.within_window',
        displayName: 'Regulatory change in the last 90 days',
        sourceColumn: 'is_reg_change',
        legacyMetricKey: 'companyBuzzIsRegChange',
        sourceIndexName: 'buzz_realtime_regulatory_change_signal_idx',
    },
    {
        key: 'signal.litigation.within_window',
        displayName: 'Litigation event in the last 90 days',
        sourceColumn: 'is_litigation',
        legacyMetricKey: 'companyBuzzIsLitigation',
        sourceIndexName: 'buzz_realtime_litigation_signal_idx',
    },
    {
        key: 'signal.financial_raise.within_window',
        displayName: 'Financing event in the last 90 days',
        sourceColumn: 'is_fin_raise',
        legacyMetricKey: 'companyBuzzIsFinRaise',
        sourceIndexName: 'buzz_realtime_financial_raise_signal_idx',
    },
    {
        key: 'signal.dividend_policy.within_window',
        displayName: 'Dividend-policy change in the last 90 days',
        sourceColumn: 'is_div_policy',
        legacyMetricKey: 'companyBuzzIsDivPolicy',
        sourceIndexName: 'buzz_realtime_dividend_policy_signal_idx',
    },
    {
        key: 'signal.revenue_guidance.within_window',
        displayName: 'Revenue guidance in the last 90 days',
        sourceColumn: 'is_rev_guide',
        legacyMetricKey: 'companyBuzzIsRevGuide',
        sourceIndexName: 'buzz_realtime_revenue_guidance_signal_idx',
    },
    {
        key: 'signal.revenue_mix.within_window',
        displayName: 'Revenue-mix change in the last 90 days',
        sourceColumn: 'is_rev_mix',
        legacyMetricKey: 'companyBuzzIsRevMix',
        sourceIndexName: 'buzz_realtime_revenue_mix_signal_idx',
    },
    {
        key: 'signal.mega_customer.within_window',
        displayName: 'Major customer win in the last 90 days',
        sourceColumn: 'is_cust_mega',
        legacyMetricKey: 'companyBuzzIsCustMega',
        sourceIndexName: 'buzz_realtime_mega_customer_signal_idx',
    },
    {
        key: 'signal.macro_risk.within_window',
        displayName: 'Macro-risk event in the last 90 days',
        sourceColumn: 'is_risk_macro',
        legacyMetricKey: 'companyBuzzIsRiskMacro',
        sourceIndexName: 'buzz_realtime_macro_risk_signal_idx',
    },
];
const realtimeBuzzEventMetricCatalog = exports.realtimeBuzzEventMetrics.map(({ key, displayName }) => ({
    key,
    displayName,
    valueType: 'boolean',
    // Buzz events contribute score; they do not determine the base company cohort.
    // This preserves the established v1 authoring contract for event signals.
    queryPhases: ['scoring'],
    supportedOperators: ['eq'],
    availability: 'authorable',
    evidencePolicy: buzzSignalEvidencePolicy,
}));
exports.realtimePrvTrademarkMetrics = [
    {
        key: 'trademark.has_any',
        displayName: 'Has PRV trademark',
        legacyMetricKey: 'prvApplicantHasTrademark',
        sourceColumn: 'has_any',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
    {
        key: 'trademark.count.total',
        displayName: 'PRV trademark count',
        legacyMetricKey: 'prvApplicantTrademarkCountTotal',
        sourceColumn: 'count_total',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'trademark.count.active',
        displayName: 'Active PRV trademark count',
        legacyMetricKey: 'prvApplicantTrademarkCountActive',
        sourceColumn: 'count_active',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'trademark.applications.last_3m_count',
        displayName: 'PRV trademark applications in the last 3 months',
        legacyMetricKey: 'prvApplicantTrademarksLast3mCount',
        sourceColumn: 'applications_last_3m_count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'trademark.applications.last_12m_count',
        displayName: 'PRV trademark applications in the last 12 months',
        legacyMetricKey: 'prvApplicantTrademarksLast12mCount',
        sourceColumn: 'applications_last_12m_count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'trademark.applied.within_3m',
        displayName: 'Applied for a PRV trademark in the last 3 months',
        legacyMetricKey: 'prvApplicantAppliedLast3m',
        sourceColumn: 'applied_within_3m',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
    {
        key: 'trademark.applied.within_12m',
        displayName: 'Applied for a PRV trademark in the last 12 months',
        legacyMetricKey: 'prvApplicantAppliedLast12m',
        sourceColumn: 'applied_within_12m',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
    {
        key: 'trademark.latest.application_date',
        displayName: 'Latest PRV trademark application date',
        legacyMetricKey: 'prvApplicantLatestApplicationDate',
        sourceColumn: 'latest_application_date',
        valueType: 'date',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn'],
    },
    {
        key: 'trademark.latest.status',
        displayName: 'Latest PRV trademark status',
        legacyMetricKey: 'prvApplicantLatestStatus',
        sourceColumn: 'latest_status',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'trademark.nice_class_count',
        displayName: 'PRV Nice class count',
        legacyMetricKey: 'prvApplicantNiceClassCount',
        sourceColumn: 'nice_class_count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'trademark.applicant.nice_classes',
        displayName: 'PRV applicant Nice classes',
        legacyMetricKey: 'prvApplicantNiceClasses',
        sourceColumn: 'nice_classes',
        valueType: 'number_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'trademark.applicant.top_marks',
        displayName: 'PRV applicant top marks',
        legacyMetricKey: 'prvApplicantTopMarks',
        sourceColumn: 'top_marks',
        valueType: 'string_array',
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
    },
    {
        key: 'trademark.next_renewal_date',
        displayName: 'Next PRV trademark renewal date',
        legacyMetricKey: 'prvApplicantNextRenewalDate',
        sourceColumn: 'next_renewal_date',
        valueType: 'date',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn'],
    },
    {
        key: 'trademark.applicant_mapping.count',
        displayName: 'PRV applicant mapping count',
        legacyMetricKey: 'prvApplicantMappedCount',
        sourceColumn: 'applicant_mapping_count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'trademark.applicant_mapping.method',
        displayName: 'PRV applicant mapping method',
        legacyMetricKey: 'prvApplicantMappingMethod',
        sourceColumn: 'applicant_mapping_method',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'trademark.applicant_mapping.last_mapped_at',
        displayName: 'PRV applicant last mapped at',
        legacyMetricKey: 'prvApplicantLastMappedAt',
        sourceColumn: 'last_mapped_at',
        valueType: 'date',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn'],
    },
];
const realtimePrvTrademarkMetricCatalog = exports.realtimePrvTrademarkMetrics.map(({ key, displayName, valueType, supportedOperators, textSearchPolicy }) => ({
    key,
    displayName,
    valueType,
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: [...supportedOperators],
    ...(textSearchPolicy === undefined ? {} : { textSearchPolicy }),
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: prvTrademarkEvidencePolicy,
}));
/**
 * Apollo has no organisation-number key. The compiler joins these facts only
 * through a current, non-deleted verified domain mapping and emits the Apollo
 * record id plus its temporal observation boundary as raw evidence.
 */
exports.realtimeApolloOrganizationMetrics = [
    {
        key: 'apollo.organization.name',
        displayName: 'Apollo company name',
        legacyMetricKey: 'apolloName',
        sourceColumn: 'name',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'apollo.organization.country',
        displayName: 'Apollo company country',
        legacyMetricKey: 'apolloCountry',
        sourceColumn: 'country',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'apollo.organization.founded_year',
        displayName: 'Apollo founded year',
        legacyMetricKey: 'apolloFoundedYear',
        sourceColumn: 'founded_year',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'apollo.organization.publicly_traded_symbol',
        displayName: 'Apollo publicly traded symbol',
        legacyMetricKey: 'apolloPubliclyTradedSymbol',
        sourceColumn: 'publicly_traded_symbol',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'apollo.organization.publicly_traded_exchange',
        displayName: 'Apollo publicly traded exchange',
        legacyMetricKey: 'apolloPubliclyTradedExchange',
        sourceColumn: 'publicly_traded_exchange',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'apollo.organization.latest_funding_stage',
        displayName: 'Apollo latest funding stage',
        legacyMetricKey: 'apolloLatestFundingStage',
        sourceColumn: 'latest_funding_stage',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'apollo.organization.latest_funding_round_date',
        displayName: 'Apollo latest funding round date',
        legacyMetricKey: 'apolloLatestFundingRoundDate',
        sourceColumn: 'latest_funding_round_date',
        valueType: 'date',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn'],
    },
    {
        key: 'apollo.organization.total_funding_printed',
        displayName: 'Apollo total funding (formatted)',
        legacyMetricKey: 'apolloTotalFundingPrinted',
        sourceColumn: 'total_funding_printed',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
];
/**
 * These are the current values from the latest Apollo observation. Historical
 * values are deliberately modelled separately below: they are Bloodline's
 * time-versioned observations, rather than a provider-reported time series.
 */
exports.realtimeApolloHeadcountCurrentMetrics = [
    {
        key: 'apollo.headcount.engineering.current',
        displayName: 'Apollo engineering headcount (current)',
        legacyMetricKey: 'apolloEngineeringHeadCountCurrent',
        sourceColumn: 'departmental_head_count_engineering',
    },
    {
        key: 'apollo.headcount.sales.current',
        displayName: 'Apollo sales headcount (current)',
        legacyMetricKey: 'apolloSalesHeadCountCurrent',
        sourceColumn: 'departmental_head_count_sales',
    },
    {
        key: 'apollo.headcount.support.current',
        displayName: 'Apollo support headcount (current)',
        legacyMetricKey: 'apolloSupportHeadCountCurrent',
        sourceColumn: 'departmental_head_count_support',
    },
    {
        key: 'apollo.headcount.marketing.current',
        displayName: 'Apollo marketing headcount (current)',
        legacyMetricKey: 'apolloMarketingHeadCountCurrent',
        sourceColumn: 'departmental_head_count_marketing',
    },
    {
        key: 'apollo.headcount.human_resources.current',
        displayName: 'Apollo human resources headcount (current)',
        legacyMetricKey: 'apolloHumanResourcesHeadCountCurrent',
        sourceColumn: 'departmental_head_count_human_resources',
    },
    {
        key: 'apollo.headcount.education.current',
        displayName: 'Apollo education headcount (current)',
        legacyMetricKey: 'apolloEducationHeadCountCurrent',
        sourceColumn: 'departmental_head_count_education',
    },
    {
        key: 'apollo.headcount.media_and_communication.current',
        displayName: 'Apollo media and communication headcount (current)',
        legacyMetricKey: 'apolloMediaAndCommunicationHeadCountCurrent',
        sourceColumn: 'departmental_head_count_media_and_commmunication',
    },
    {
        key: 'apollo.headcount.consulting.current',
        displayName: 'Apollo consulting headcount (current)',
        legacyMetricKey: 'apolloConsultingHeadCountCurrent',
        sourceColumn: 'departmental_head_count_consulting',
    },
    {
        key: 'apollo.headcount.operations.current',
        displayName: 'Apollo operations headcount (current)',
        legacyMetricKey: 'apolloOperationsHeadCountCurrent',
        sourceColumn: 'departmental_head_count_operations',
    },
    {
        key: 'apollo.headcount.finance.current',
        displayName: 'Apollo finance headcount (current)',
        legacyMetricKey: 'apolloFinanceHeadCountCurrent',
        sourceColumn: 'departmental_head_count_finance',
    },
    {
        key: 'apollo.headcount.product_management.current',
        displayName: 'Apollo product management headcount (current)',
        legacyMetricKey: 'apolloProductManagementHeadCountCurrent',
        sourceColumn: 'departmental_head_count_product_management',
    },
    {
        key: 'apollo.headcount.business_development.current',
        displayName: 'Apollo business development headcount (current)',
        legacyMetricKey: 'apolloBusinessDevelopmentHeadCountCurrent',
        sourceColumn: 'departmental_head_count_business_development',
    },
    {
        key: 'apollo.headcount.information_technology.current',
        displayName: 'Apollo information technology headcount (current)',
        legacyMetricKey: 'apolloInformationTechnologyHeadCountCurrent',
        sourceColumn: 'departmental_head_count_information_technology',
    },
    {
        key: 'apollo.headcount.data_science.current',
        displayName: 'Apollo data science headcount (current)',
        legacyMetricKey: 'apolloDataScienceHeadCountCurrent',
        sourceColumn: 'departmental_head_count_data_science',
    },
    {
        key: 'apollo.headcount.arts_and_design.current',
        displayName: 'Apollo arts and design headcount (current)',
        legacyMetricKey: 'apolloArtsAndDesignHeadCountCurrent',
        sourceColumn: 'departmental_head_count_arts_and_design',
    },
    {
        key: 'apollo.headcount.entrepreneurship.current',
        displayName: 'Apollo entrepreneurship headcount (current)',
        legacyMetricKey: 'apolloEntrepreneurshipHeadCountCurrent',
        sourceColumn: 'departmental_head_count_entrepreneurship',
    },
    {
        key: 'apollo.headcount.administrative.current',
        displayName: 'Apollo administrative headcount (current)',
        legacyMetricKey: 'apolloAdministrativeHeadCountCurrent',
        sourceColumn: 'departmental_head_count_administrative',
    },
    {
        key: 'apollo.headcount.legal.current',
        displayName: 'Apollo legal headcount (current)',
        legacyMetricKey: 'apolloLegalHeadCountCurrent',
        sourceColumn: 'departmental_head_count_legal',
    },
    {
        key: 'apollo.headcount.accounting.current',
        displayName: 'Apollo accounting headcount (current)',
        legacyMetricKey: 'apolloAccountingHeadCountCurrent',
        sourceColumn: 'departmental_head_count_accounting',
    },
    {
        key: 'apollo.headcount.total.current',
        displayName: 'Apollo total headcount (current)',
        legacyMetricKey: 'apolloTotalHeadCountCurrent',
        sourceColumn: 'estimated_num_employees',
    },
];
const apolloOrganizationEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['apollo'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const realtimeApolloOrganizationMetricCatalog = exports.realtimeApolloOrganizationMetrics.map(({ key, displayName, valueType, supportedOperators }) => ({
    key,
    displayName,
    valueType,
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: [...supportedOperators],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: apolloOrganizationEvidencePolicy,
}));
const realtimeApolloHeadcountCurrentMetricCatalog = exports.realtimeApolloHeadcountCurrentMetrics.map(({ key, displayName }) => ({
    key,
    displayName,
    valueType: 'number',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: apolloOrganizationEvidencePolicy,
}));
exports.realtimeApolloHeadcountHistoricalPeriods = [
    {
        keySuffix: 'one_month_ago',
        legacyKeySuffix: '1MonthAgo',
        displayLabel: 'one month ago',
        postgresInterval: '1 month',
    },
    {
        keySuffix: 'three_months_ago',
        legacyKeySuffix: '3MonthsAgo',
        displayLabel: 'three months ago',
        postgresInterval: '3 months',
    },
    {
        keySuffix: 'six_months_ago',
        legacyKeySuffix: '6MonthsAgo',
        displayLabel: 'six months ago',
        postgresInterval: '6 months',
    },
    {
        keySuffix: 'nine_months_ago',
        legacyKeySuffix: '9MonthsAgo',
        displayLabel: 'nine months ago',
        postgresInterval: '9 months',
    },
    {
        keySuffix: 'twelve_months_ago',
        legacyKeySuffix: '12MonthsAgo',
        displayLabel: 'twelve months ago',
        postgresInterval: '12 months',
    },
];
const apolloHeadcountMetricDimension = (key) => key.split('.')[2];
const legacyHistoricalApolloHeadcountKey = (legacyMetricKey, period) => legacyMetricKey.replace(/Current$/, period.legacyKeySuffix);
exports.realtimeApolloHeadcountHistoricalMetrics = exports.realtimeApolloHeadcountHistoricalPeriods.flatMap((period) => exports.realtimeApolloHeadcountCurrentMetrics.map((metric) => ({
    key: `apollo.headcount.${apolloHeadcountMetricDimension(metric.key)}.${period.keySuffix}`,
    displayName: `Apollo ${metric.displayName.slice('Apollo '.length, -' (current)'.length)} (observed ${period.displayLabel})`,
    legacyMetricKey: legacyHistoricalApolloHeadcountKey(metric.legacyMetricKey, period),
    sourceColumn: metric.sourceColumn,
    period,
})));
const legacyGrowthApolloHeadcountKey = (legacyMetricKey, period) => legacyMetricKey.replace(/Current$/, `Growth${period.legacyKeySuffix.replace(/Ago$/, '')}`);
exports.realtimeApolloHeadcountGrowthMetrics = exports.realtimeApolloHeadcountHistoricalMetrics.map((historicalMetric) => {
    const currentMetric = exports.realtimeApolloHeadcountCurrentMetrics.find((metric) => metric.sourceColumn === historicalMetric.sourceColumn);
    if (currentMetric === undefined) {
        throw new Error(`Missing current Apollo headcount metric for ${historicalMetric.key}`);
    }
    return {
        key: `apollo.headcount.${apolloHeadcountMetricDimension(currentMetric.key)}.growth_${historicalMetric.period.keySuffix.replace('_ago', '')}`,
        displayName: `Apollo ${currentMetric.displayName.slice('Apollo '.length, -' (current)'.length)} growth (${historicalMetric.period.displayLabel})`,
        legacyMetricKey: legacyGrowthApolloHeadcountKey(currentMetric.legacyMetricKey, historicalMetric.period),
        currentMetricKey: currentMetric.key,
        historicalMetricKey: historicalMetric.key,
        period: historicalMetric.period,
    };
});
const apolloHistoricalObservationEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['apollo'],
    temporalSemantics: 'historical_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const apolloHeadcountGrowthEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['apollo'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const realtimeApolloHeadcountHistoricalMetricCatalog = exports.realtimeApolloHeadcountHistoricalMetrics.map(({ key, displayName }) => ({
    key,
    displayName,
    valueType: 'number',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: apolloHistoricalObservationEvidencePolicy,
}));
const realtimeApolloHeadcountGrowthMetricCatalog = exports.realtimeApolloHeadcountGrowthMetrics.map(({ key, displayName }) => ({
    key,
    displayName,
    valueType: 'number',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: apolloHeadcountGrowthEvidencePolicy,
}));
exports.realtimeLinkedInCurrentMetrics = [
    {
        key: 'linkedin.profile.handle',
        displayName: 'LinkedIn profile handle',
        legacyMetricKey: 'linkedinId',
        sourceColumn: 'linkedin_handle',
        sourceObservationIdColumn: 'linkedin_handle_observation_id',
        sourceObservedAtColumn: 'linkedin_handle_observed_at',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'linkedin.profile.website_domain',
        displayName: 'LinkedIn profile website domain',
        legacyMetricKey: 'linkedinDomain',
        sourceColumn: 'website_domain',
        sourceObservationIdColumn: 'website_domain_observation_id',
        sourceObservedAtColumn: 'website_domain_observed_at',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'linkedin.profile.industry',
        displayName: 'LinkedIn industry',
        legacyMetricKey: 'linkedinIndustry',
        sourceColumn: 'industry',
        sourceObservationIdColumn: 'industry_observation_id',
        sourceObservedAtColumn: 'industry_observed_at',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'linkedin.profile.employee_count',
        displayName: 'LinkedIn employee count',
        legacyMetricKey: 'linkedinNumberOfEmployees',
        sourceColumn: 'number_of_employees',
        sourceObservationIdColumn: 'number_of_employees_observation_id',
        sourceObservedAtColumn: 'number_of_employees_observed_at',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        // LinkedIn page verification is a first-party profile fact. It is not the
        // same as Bloodline's historical organisation-to-handle verification, so
        // it deliberately has no legacy field mapping.
        key: 'linkedin.profile.page_verified',
        displayName: 'LinkedIn page verified',
        sourceColumn: 'page_verified',
        sourceObservationIdColumn: 'page_verified_observation_id',
        sourceObservedAtColumn: 'page_verified_observed_at',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
];
exports.realtimeLinkedInEmployeeGrowthPeriods = [
    {
        keySuffix: '3m',
        legacyMetricKey: 'linkedinAllEmployeeGrowth3Months',
        displayLabel: '3 months',
        postgresInterval: '3 months',
        captureTolerance: '14 days',
    },
    {
        keySuffix: '6m',
        legacyMetricKey: 'linkedinAllEmployeeGrowth6Months',
        displayLabel: '6 months',
        postgresInterval: '6 months',
        captureTolerance: '14 days',
    },
    {
        keySuffix: '1y',
        legacyMetricKey: 'linkedinAllEmployeeGrowth1Year',
        displayLabel: '1 year',
        postgresInterval: '1 year',
        captureTolerance: '14 days',
    },
    {
        keySuffix: '2y',
        legacyMetricKey: 'linkedinAllEmployeeGrowth2Years',
        displayLabel: '2 years',
        postgresInterval: '2 years',
        captureTolerance: '14 days',
    },
];
exports.realtimeLinkedInEmployeeGrowthMetrics = exports.realtimeLinkedInEmployeeGrowthPeriods.map((period) => ({
    key: `linkedin.profile.employee_growth_${period.keySuffix}`,
    displayName: `LinkedIn employee growth, ${period.displayLabel}`,
    legacyMetricKey: period.legacyMetricKey,
    period,
}));
const linkedInCurrentEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['linkedin'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const linkedInEmployeeGrowthEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['linkedin'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const realtimeLinkedInCurrentMetricCatalog = exports.realtimeLinkedInCurrentMetrics.map(({ key, displayName, valueType, supportedOperators }) => ({
    key,
    displayName,
    valueType,
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: [...supportedOperators],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: linkedInCurrentEvidencePolicy,
}));
const realtimeLinkedInMappingVerificationMetricCatalog = [
    {
        key: 'linkedin.identity.mapping_verified',
        displayName: 'LinkedIn company mapping verified',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: linkedInCurrentEvidencePolicy,
    },
];
const realtimeLinkedInEmployeeGrowthMetricCatalog = exports.realtimeLinkedInEmployeeGrowthMetrics.map(({ key, displayName }) => ({
    key,
    displayName,
    valueType: 'number',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: linkedInEmployeeGrowthEvidencePolicy,
}));
exports.realtimeGooglePlacesCurrentMetrics = [
    {
        key: 'google_places.company_name',
        displayName: 'Google Place name',
        sourceColumn: 'company_name',
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
    },
    {
        key: 'google_places.formatted_address',
        displayName: 'Google Place formatted address',
        sourceColumn: 'formatted_address',
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
    },
    {
        key: 'google_places.address',
        displayName: 'Google Place street address',
        sourceColumn: 'address',
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
    },
    {
        key: 'google_places.postal_code',
        displayName: 'Google Place postal code',
        sourceColumn: 'postal_code',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
    {
        key: 'google_places.city',
        displayName: 'Google Place city',
        sourceColumn: 'city',
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
    },
    {
        key: 'google_places.country',
        displayName: 'Google Place country',
        sourceColumn: 'country',
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
    },
];
const googlePlacesCurrentEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['google_places'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const realtimeGooglePlacesCurrentMetricCatalog = exports.realtimeGooglePlacesCurrentMetrics.map(({ key, displayName, supportedOperators, textSearchPolicy }) => ({
    key,
    displayName,
    valueType: 'string',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: [...supportedOperators],
    ...(textSearchPolicy === undefined ? {} : { textSearchPolicy }),
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: googlePlacesCurrentEvidencePolicy,
}));
const workplaceEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['company_workplace'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const jobtechActiveAdsEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['jobtech_active_ads'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const jobtechActiveAdsDerivedEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['jobtech_active_ads', 'valu8_company_employees'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const careerHiringEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['career_hiring'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
const careerHiringDerivedEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['career_hiring'],
    temporalSemantics: 'derived',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
/** A successful, mapped website crawl. `observedAt` is the scrape timestamp. */
const domainCrawlEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['domains'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
/** A domain-to-company mapping observation, independent of a website crawl. */
const domainMappingEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['domain_orgnr_map'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
exports.realtimeDomainToolMetrics = [
    ['cookiebot', 'Cookiebot', 'toolCookiebot', 'tool_cookiebot'],
    ['cookielaw', 'CookieLaw', 'toolCookielaw', 'tool_cookielaw'],
    ['onetrust', 'OneTrust', 'toolOnetrust', 'tool_onetrust'],
    ['doubleclick', 'DoubleClick', 'toolDoubleclick', 'tool_doubleclick'],
    ['ebbot', 'Ebbot', 'toolEbbot', 'tool_ebbot'],
    ['intercom', 'Intercom', 'toolIntercom', 'tool_intercom'],
    ['marketo', 'Marketo', 'toolMarketo', 'tool_marketo'],
    ['trustpilot', 'Trustpilot', 'toolTrustpilot', 'tool_trustpilot'],
    ['custobar', 'Custobar', 'toolCustobar', 'tool_custobar'],
    ['voyado', 'Voyado', 'toolVoyado', 'tool_voyado'],
    ['godaddy', 'GoDaddy', 'toolGodaddy', 'tool_godaddy'],
    ['squarespace', 'Squarespace', 'toolSquarespace', 'tool_squarespace'],
    ['wix', 'Wix', 'toolWix', 'tool_wix'],
    ['seenthis', 'Seenthis', 'toolSeenthis', 'tool_seenthis'],
    ['yotpo', 'Yotpo', 'toolYotpo', 'tool_yotpo'],
    ['apprl', 'Apprl', 'toolApprl', 'tool_apprl'],
    ['criteo', 'Criteo', 'toolCriteo', 'tool_criteo'],
    ['reco', 'Reco', 'toolReco', 'tool_reco'],
].map(([slug, displayName, legacyMetricKey, sourceColumn]) => ({
    key: `web.tools.${slug}`,
    displayName: `${displayName} detected`,
    legacyMetricKey,
    sourceColumn: sourceColumn,
}));
const realtimeDomainToolMetricCatalog = exports.realtimeDomainToolMetrics.map(({ key, displayName }) => ({
    key,
    displayName,
    valueType: 'boolean',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq'],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy: domainCrawlEvidencePolicy,
}));
exports.realtimeDomainAnalyticsMetrics = [
    {
        key: 'web.analytics.universal_analytics_count',
        displayName: 'Universal Analytics detections',
        sourceColumn: 'analytics_ua',
        legacyMetricKey: 'analyticsUa',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.google_analytics_4_count',
        displayName: 'Google Analytics 4 detections',
        sourceColumn: 'analytics_ga4',
        legacyMetricKey: 'analyticsGa4',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.meta_pixel_count',
        displayName: 'Meta Pixel detections',
        sourceColumn: 'analytics_fb',
        legacyMetricKey: 'analyticsFb',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.gconvp_account',
        displayName: 'GCONVP account',
        sourceColumn: 'gconvp_account',
        legacyMetricKey: 'gconvpAccount',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.analytics.adobe_account',
        displayName: 'Adobe account',
        sourceColumn: 'adobe_account',
        legacyMetricKey: 'adobeAccount',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.analytics.google_tag_manager_count',
        displayName: 'Google Tag Manager detections',
        sourceColumn: 'analytics_gtm',
        legacyMetricKey: 'analyticsGtm',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.google_ads_count',
        displayName: 'Google Ads detections',
        sourceColumn: 'analytics_aw',
        legacyMetricKey: 'analyticsAw',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.matomo_count',
        displayName: 'Matomo detections',
        sourceColumn: 'analytics_mo',
        legacyMetricKey: 'analyticsMo',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.adobe_analytics_count',
        displayName: 'Adobe Analytics detections',
        sourceColumn: 'analytics_adobe',
        legacyMetricKey: 'analyticsAdobe',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.optimizely_count',
        displayName: 'Optimizely detections',
        sourceColumn: 'analytics_opt',
        legacyMetricKey: 'analyticsOpt',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.amplitude_count',
        displayName: 'Amplitude detections',
        sourceColumn: 'analytics_amplitude',
        legacyMetricKey: 'analyticsAmplitude',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.analytics.snowplow_count',
        displayName: 'Snowplow detections',
        sourceColumn: 'analytics_snowplow',
        legacyMetricKey: 'analyticsSnowplow',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
];
const realtimeDomainAnalyticsMetricCatalog = exports.realtimeDomainAnalyticsMetrics.map(({ key, displayName, valueType, supportedOperators, legacyMetricKey }) => legacyMetricKey === 'gconvpAccount'
    ? {
        key,
        displayName,
        valueType,
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: [...supportedOperators],
        availability: 'planned',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    }
    : {
        key,
        displayName,
        valueType,
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: [...supportedOperators],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    });
exports.realtimeDomainMetadataMetrics = [
    {
        key: 'web.domain.mapping_verified',
        displayName: 'Company-domain mapping verified',
        sourceColumn: 'mapping_verified',
        legacyMetricKey: 'domainIsVerified',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
        evidencePolicy: domainMappingEvidencePolicy,
    },
    {
        key: 'web.page.sitemap_url',
        displayName: 'Website sitemap URL',
        sourceColumn: 'sitemap',
        legacyMetricKey: 'sitemap',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.sitemap_count',
        displayName: 'Discovered website sitemap count',
        sourceColumn: 'sitemap_count',
        legacyMetricKey: 'sitemapCount',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.sitemap_url_count',
        displayName: 'URLs found in website sitemaps',
        sourceColumn: 'sitemap_url_count',
        legacyMetricKey: 'sitemapUrlCount',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.privacy.trackers_before_consent',
        displayName: 'Third-party trackers observed before consent',
        sourceColumn: 'trackers_before_consent',
        legacyMetricKey: 'trackersBeforeConsent',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.canonical_url',
        displayName: 'Canonical URL',
        sourceColumn: 'canonical',
        legacyMetricKey: 'canonical',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.robots_directives',
        displayName: 'Robots directives',
        sourceColumn: 'robots',
        legacyMetricKey: 'robots',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.noindex',
        displayName: 'Page blocks search indexing',
        sourceColumn: 'noindex',
        legacyMetricKey: 'noindex',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
    {
        key: 'web.page.schema_script_detected',
        displayName: 'JSON-LD schema script detected',
        sourceColumn: 'has_schema_script',
        legacyMetricKey: 'hasSchemaScript',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
    {
        key: 'web.page.x_robots_tag',
        displayName: 'X-Robots-Tag header',
        sourceColumn: 'x_robots_tag',
        legacyMetricKey: 'xRobotsTag',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.server',
        displayName: 'Web server',
        sourceColumn: 'server',
        legacyMetricKey: 'server',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.framework',
        displayName: 'Framework reported by X-Powered-By',
        sourceColumn: 'framework',
        legacyMetricKey: 'framework',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.host',
        displayName: 'Web host',
        sourceColumn: 'host',
        legacyMetricKey: 'host',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.requested_hosts',
        displayName: 'Hosts requested during website crawl',
        sourceColumn: 'requested_hosts',
        legacyMetricKey: 'requestedHosts',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.title',
        displayName: 'Page title',
        sourceColumn: 'title',
        legacyMetricKey: 'title',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.description',
        displayName: 'Meta description',
        sourceColumn: 'description',
        legacyMetricKey: 'description',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.language',
        displayName: 'Page language',
        sourceColumn: 'lang',
        legacyMetricKey: 'lang',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.favicon',
        displayName: 'Favicon URL',
        sourceColumn: 'favicon',
        legacyMetricKey: 'favicon',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.keywords',
        displayName: 'Page keywords',
        sourceColumn: 'keywords',
        legacyMetricKey: 'domainKeywords',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.open_graph_image',
        displayName: 'Open Graph image URL',
        sourceColumn: 'org_image',
        legacyMetricKey: 'orgImage',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.page.font_colors',
        displayName: 'Website font colors',
        sourceColumn: 'colors_font',
        legacyMetricKey: 'colorsFont',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.page_colors',
        displayName: 'Website page colors',
        sourceColumn: 'colors_page',
        legacyMetricKey: 'colorsPage',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.svg_colors',
        displayName: 'Website SVG colors',
        sourceColumn: 'colors_svg',
        legacyMetricKey: 'colorsSvg',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.internal_link_count',
        displayName: 'Internal link count',
        sourceColumn: 'internal_links',
        legacyMetricKey: 'internalLinks',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.external_link_count',
        displayName: 'External link count',
        sourceColumn: 'external_links',
        legacyMetricKey: 'externalLinks',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.title_word_count',
        displayName: 'Page title word count',
        sourceColumn: 'title_words',
        legacyMetricKey: 'titleWords',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.title_character_count',
        displayName: 'Page title character count',
        sourceColumn: 'title_chars',
        legacyMetricKey: 'titleChars',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.description_word_count',
        displayName: 'Meta description word count',
        sourceColumn: 'description_words',
        legacyMetricKey: 'descriptionWords',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.description_character_count',
        displayName: 'Meta description character count',
        sourceColumn: 'description_chars',
        legacyMetricKey: 'descriptionChars',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.h1_heading_count',
        displayName: 'H1 heading count',
        sourceColumn: 'h_1_count',
        legacyMetricKey: 'h1Count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.h2_heading_count',
        displayName: 'H2 heading count',
        sourceColumn: 'h_2_count',
        legacyMetricKey: 'h2Count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.h3_heading_count',
        displayName: 'H3 heading count',
        sourceColumn: 'h_3_count',
        legacyMetricKey: 'h3Count',
        valueType: 'number',
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    },
    {
        key: 'web.page.h1_content',
        displayName: 'H1 heading text',
        sourceColumn: 'h_1_content',
        legacyMetricKey: 'h1Content',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.h1_content_word_counts',
        displayName: 'H1 heading word counts',
        sourceColumn: 'h_1_content_words',
        legacyMetricKey: 'h1ContentWords',
        valueType: 'number_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.page.h1_content_character_counts',
        displayName: 'H1 heading character counts',
        sourceColumn: 'h_1_content_chars',
        legacyMetricKey: 'h1ContentChars',
        valueType: 'number_array',
        supportedOperators: ['contains'],
    },
    // The active crawler captures the first A record as the canonical IPv4; the
    // source observation is the successful domain scrape, not a request-time DNS lookup.
    {
        key: 'web.infrastructure.ip_address',
        displayName: 'Website IP address',
        sourceColumn: 'ip_address',
        legacyMetricKey: 'ipAddress',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.hosting_provider',
        displayName: 'Hosting provider',
        sourceColumn: 'hosting_provider',
        legacyMetricKey: 'hostingProvider',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
        availability: 'planned',
    },
    {
        key: 'web.infrastructure.hosting_region',
        displayName: 'Hosting region',
        sourceColumn: 'hosting_region',
        legacyMetricKey: 'hostingRegion',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
        availability: 'planned',
    },
    {
        key: 'web.infrastructure.dns_a_records',
        displayName: 'DNS A records',
        sourceColumn: 'dns_a',
        legacyMetricKey: 'dnsA',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.infrastructure.dns_aaaa_records',
        displayName: 'DNS AAAA records',
        sourceColumn: 'dns_aaaa',
        legacyMetricKey: 'dnsAaaa',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.infrastructure.dns_ns_records',
        displayName: 'DNS NS records',
        sourceColumn: 'dns_ns',
        legacyMetricKey: 'dnsNs',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.infrastructure.dns_soa_record',
        displayName: 'DNS SOA record',
        sourceColumn: 'dns_soa',
        legacyMetricKey: 'dnsSoa',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.dns_mx_record',
        displayName: 'DNS MX record',
        sourceColumn: 'dns_mx',
        legacyMetricKey: 'dnsMx',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.email_provider',
        displayName: 'Email provider',
        sourceColumn: 'email_provider',
        legacyMetricKey: 'emailProvider',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.dns_spf_records',
        displayName: 'DNS SPF records',
        sourceColumn: 'dns_spf',
        legacyMetricKey: 'dnsSpf',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
    {
        key: 'web.infrastructure.dns_dmarc_configured',
        displayName: 'DMARC configured',
        sourceColumn: 'dns_dmarc',
        legacyMetricKey: 'dnsDmarc',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
    },
    {
        key: 'web.infrastructure.dns_dmarc_level',
        displayName: 'DMARC policy level',
        sourceColumn: 'dns_dmarc_level',
        legacyMetricKey: 'dnsDmarcLevel',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.dns_dmarc_details',
        displayName: 'DMARC details',
        sourceColumn: 'dns_dmarc_details',
        legacyMetricKey: 'dnsDmarcDetails',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.dns_dkim_configured',
        displayName: 'DKIM configured',
        sourceColumn: 'dns_dkim',
        legacyMetricKey: 'dnsDkim',
        valueType: 'boolean',
        supportedOperators: ['eq', 'neq'],
        // A null means either no known selector matched or the DNS lookup was
        // unavailable, so this cannot safely claim `false` yet.
        availability: 'planned',
    },
    {
        key: 'web.infrastructure.dns_dkim_selector',
        displayName: 'Observed DKIM selector',
        sourceColumn: 'dns_dkim_selector',
        legacyMetricKey: 'dnsDkimSelector',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.dns_dkim_level',
        displayName: 'Observed DKIM key strength',
        sourceColumn: 'dns_dkim_level',
        legacyMetricKey: 'dnsDkimLevel',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
        observedValues: ['Strong', 'Moderate', 'Weak'],
    },
    {
        key: 'web.infrastructure.dns_dkim_details',
        displayName: 'Observed DKIM record details',
        sourceColumn: 'dns_dkim_details',
        legacyMetricKey: 'dnsDkimDetails',
        valueType: 'string',
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
    },
    {
        key: 'web.infrastructure.dns_verifications',
        displayName: 'DNS verification details',
        sourceColumn: 'dns_verifications',
        legacyMetricKey: 'dnsVerifications',
        valueType: 'string_array',
        supportedOperators: ['contains'],
    },
];
const realtimeDomainMetadataMetricCatalog = exports.realtimeDomainMetadataMetrics.map(({ key, displayName, valueType, supportedOperators, observedValues, availability, evidencePolicy }) => ({
    key,
    displayName,
    valueType,
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: [...supportedOperators],
    ...(observedValues ? { observedValues: [...observedValues] } : {}),
    availability: availability ?? 'authorable',
    coverage: 'sparse',
    evidencePolicy: evidencePolicy ?? domainCrawlEvidencePolicy,
}));
const crmWorkspaceEvidencePolicy = new RealtimeMetricEvidencePolicy({
    sourceSystems: ['pipedrive'],
    temporalSemantics: 'current_observation',
    requiresObservedAt: true,
    requiresSourceRecordId: true,
});
exports.realtimeGroupMetrics = [
    {
        key: 'company.group.identified_orgnr_count',
        displayName: 'Identified organisation numbers in company group',
        lookbackInterval: undefined,
        evidencePolicy: valu8GroupStructureEvidencePolicy,
    },
    {
        key: 'company.group.identified_orgnr_delta_6m',
        displayName: 'Change in identified organisation numbers in company group, 6 months',
        lookbackInterval: '6 months',
        evidencePolicy: valu8GroupStructureHistoricalEvidencePolicy,
    },
    {
        key: 'company.group.identified_orgnr_delta_1y',
        displayName: 'Change in identified organisation numbers in company group, 1 year',
        lookbackInterval: '1 year',
        evidencePolicy: valu8GroupStructureHistoricalEvidencePolicy,
    },
    {
        key: 'company.group.identified_orgnr_delta_3y',
        displayName: 'Change in identified organisation numbers in company group, 3 years',
        lookbackInterval: '3 years',
        evidencePolicy: valu8GroupStructureHistoricalEvidencePolicy,
    },
    {
        key: 'company.group.identified_orgnr_delta_5y',
        displayName: 'Change in identified organisation numbers in company group, 5 years',
        lookbackInterval: '5 years',
        evidencePolicy: valu8GroupStructureHistoricalEvidencePolicy,
    },
];
/** Direct parent-company facts from the current Valu8 group-structure snapshot. */
exports.realtimeGroupParentMetrics = [
    {
        key: 'company.ownership.parent_company_name',
        displayName: 'Parent company name',
        evidencePolicy: valu8GroupStructureEvidencePolicy,
    },
];
const realtimeGroupMetricCatalog = exports.realtimeGroupMetrics.map(({ key, displayName, evidencePolicy }) => ({
    key,
    displayName,
    valueType: 'number',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy,
}));
exports.realtimeGroupEventSignalMetrics = [
    {
        key: 'signal.group_growth.within_1y',
        displayName: 'Observed group growth in the last year',
        eventType: 'group_member_added',
        lookbackInterval: '1 year',
        evidencePolicy: valu8GroupEventEvidencePolicy,
    },
    {
        key: 'signal.group_country_entry.within_18m',
        displayName: 'Observed group entry into a new country in the last 18 months',
        eventType: 'group_country_entered',
        lookbackInterval: '18 months',
        evidencePolicy: valu8GroupEventEvidencePolicy,
    },
];
// Snapshot-derived events establish positive signals; their absence cannot
// establish a negative, so the compiler returns null when no event is found.
const realtimeGroupEventSignalMetricCatalog = exports.realtimeGroupEventSignalMetrics.map(({ key, displayName, evidencePolicy }) => ({
    key,
    displayName,
    valueType: 'boolean',
    queryPhases: ['scoring'],
    supportedOperators: ['eq'],
    booleanValuePolicy: 'positive_only',
    availability: 'authorable',
    coverage: 'sparse',
    evidencePolicy,
}));
/**
 * Backend-owned metadata for a metric that can appear in a scoring definition.
 * The catalog intentionally contains no SQL so that the definition package can
 * be shared with clients without granting query execution capability.
 */
const realtimeMetricDefinitionFields = {
    key: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    displayName: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    valueType: model_1.MetricValueType,
    queryPhases: effect_1.Schema.Array(exports.MetricQueryPhase).pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    supportedOperators: effect_1.Schema.Array(model_1.MetricOperator),
    textSearchPolicy: effect_1.Schema.optional(exports.MetricTextSearchPolicy),
    currencyPolicy: effect_1.Schema.optional(exports.MetricCurrencyPolicy),
    workforceFactPolicy: effect_1.Schema.optional(exports.RealtimeWorkforceFactPolicy),
    observedValues: effect_1.Schema.optional(effect_1.Schema.Array(effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))))),
    /** Restricts positive event signals to a true predicate; missing observations are unknown. */
    booleanValuePolicy: effect_1.Schema.optional(effect_1.Schema.Literal('positive_only')),
    /** Omitted metrics are global. Client scope is enforced again by the compiler. */
    dataScope: effect_1.Schema.optional(exports.MetricDataScope),
    /** Omitted metrics are `broad`. See {@link MetricCoverage}. */
    coverage: effect_1.Schema.optional(exports.MetricCoverage),
};
const _RealtimeMetricDefinitionBase = effect_1.Schema.Struct({
    ...realtimeMetricDefinitionFields,
    availability: exports.MetricAvailability,
    evidencePolicy: effect_1.Schema.optional(RealtimeMetricEvidencePolicy),
});
/**
 * Runtime API contract for catalog entries. The authorable branch is
 * deliberately discriminated: omitting evidence makes a metric non-authorable
 * at decode time as well as in validation.
 */
exports.RealtimeMetricDefinition = effect_1.Schema.Union([
    effect_1.Schema.Struct({
        ...realtimeMetricDefinitionFields,
        availability: effect_1.Schema.Literal('authorable'),
        evidencePolicy: RealtimeMetricEvidencePolicy,
    }),
    effect_1.Schema.Struct({
        ...realtimeMetricDefinitionFields,
        availability: effect_1.Schema.Literals(['planned', 'deferred']),
        evidencePolicy: effect_1.Schema.optional(RealtimeMetricEvidencePolicy),
    }),
]);
const financialStatementMetric = (key, displayName) => ({
    key,
    displayName,
    valueType: 'money',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    currencyPolicy: 'same_currency_only',
    evidencePolicy: financialStatementEvidencePolicy,
});
const financialDerivedMetric = (key, displayName) => ({
    key,
    displayName,
    valueType: 'number',
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    evidencePolicy: financialDerivedEvidencePolicy,
});
const financialReportingMetadataMetric = (key, displayName, valueType) => ({
    key,
    displayName,
    valueType,
    queryPhases: ['eligibility', 'scoring'],
    supportedOperators: valueType === 'string' ? ['eq', 'neq'] : ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
    availability: 'authorable',
    evidencePolicy: financialStatementEvidencePolicy,
});
const careerHiringPeriodLabel = {
    previous: 'Previous observation',
    '1m': '1 month',
    '3m': '3 months',
    '6m': '6 months',
    '1y': '1 year',
};
/**
 * The initial allow-list. New metrics must be added here and implemented by a
 * backend compiler before a saved definition can use them.
 */
exports.realtimeMetricCatalog = [
    {
        key: 'company.identity.orgnr',
        displayName: 'Organization number (including country code)',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.identity.name',
        displayName: 'Company name',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        // Literal substrings with at least three non-whitespace characters are
        // accepted so the dedicated trigram index remains effective.
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.identity.previous_names',
        displayName: 'Previous company names',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.registry.keywords.current',
        displayName: 'Company keywords',
        valueType: 'string_array',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        availability: 'authorable',
        evidencePolicy: valu8CompanyEvidencePolicy,
    },
    {
        key: 'company.location.address',
        displayName: 'Address',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.contact.website',
        displayName: 'Company website',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.contact.phone',
        displayName: 'Company phone',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.contact.email',
        displayName: 'Company email',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.profile.description_en',
        displayName: 'Company description (English)',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.industry.sni_code',
        displayName: 'SNI code',
        valueType: 'string',
        queryPhases: ['eligibility'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.industry.sni_prefix',
        displayName: 'SNI code prefix',
        valueType: 'string',
        queryPhases: ['eligibility'],
        supportedOperators: ['in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.industry.sni_classification',
        displayName: 'SNI classification',
        valueType: 'string',
        queryPhases: ['eligibility'],
        // `contains` mirrors legacy `includes` and `includesOneOf`: case-insensitive
        // substring matching against the raw company_master classification text.
        supportedOperators: ['eq', 'neq', 'in', 'notIn', 'contains'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.operational_status',
        displayName: 'Operational status',
        valueType: 'string',
        queryPhases: ['eligibility'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        observedValues: [
            'Active',
            'Bankrupt',
            'Banktrupt',
            'Dissolved',
            'DissolvedByForce',
            'DissolvingByForce',
            'InactiveSleeping',
            'Liquidated',
            'Merged',
            'Sleeping',
            'UnderBankruptcy',
            'UnderLiquidation',
            'UnderReconstruction',
            'Unregistered',
        ],
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.legal_entity_type',
        displayName: 'Legal entity type',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.category',
        displayName: 'Company category',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.structure',
        displayName: 'Company structure',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.legal_form',
        displayName: 'Legal form',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.listing_type',
        displayName: 'Listing type',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.registration_date',
        displayName: 'Registration date',
        valueType: 'date',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.ownership.has_private_equity_owner',
        displayName: 'Has private equity owner',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.ownership.private_equity_owner',
        displayName: 'Private equity owner',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.ownership.has_foreign_ultimate_parent',
        displayName: 'Has foreign ultimate parent',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.ownership.owner_count',
        displayName: 'Number of owners',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.ownership.ultimate_parent_company_name',
        displayName: 'Ultimate parent company name',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: valu8CompanyEvidencePolicy,
    },
    {
        key: 'company.ownership.ultimate_parent_orgnr',
        displayName: 'Ultimate parent organisation number',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: valu8CompanyEvidencePolicy,
    },
    {
        key: 'company.ownership.parent_company_name',
        displayName: 'Parent company name',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: valu8GroupStructureEvidencePolicy,
    },
    {
        key: 'company.leadership.ceo_name',
        displayName: 'Chief executive officer',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.leadership.ceo_birth_year',
        displayName: 'Chief executive officer birth year',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.leadership.chairman_name',
        displayName: 'Chairman',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.leadership.signatory',
        displayName: 'Authorized signatory',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.leadership.chairman_birth_year',
        displayName: 'Chairman birth year',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.capital.has_share_issues',
        displayName: 'Has share issues',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.capital.share_count',
        displayName: 'Number of shares',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.location.postal_code',
        displayName: 'Postal code',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.location.municipality',
        displayName: 'Municipality',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.location.county',
        displayName: 'County',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.location.region',
        displayName: 'Region',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.country.iso3166',
        displayName: 'Country (ISO 3166-1 alpha-2)',
        valueType: 'string',
        queryPhases: ['eligibility'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'company.location.city',
        displayName: 'City',
        valueType: 'string',
        queryPhases: ['eligibility'],
        // `contains` intentionally mirrors legacy `includes`: case-insensitive
        // substring matching, backed by the raw company_master.city column.
        supportedOperators: ['contains'],
        availability: 'authorable',
        evidencePolicy: companyMasterEvidencePolicy,
    },
    {
        key: 'web.domain.current',
        displayName: 'Website domain',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.social.snapchat_handles',
        displayName: 'Snapchat handles',
        valueType: 'string_array',
        queryPhases: ['eligibility', 'scoring'],
        // One company may publish several handles. `contains` matches one handle
        // at a time and keeps the result projection as the original string array.
        supportedOperators: ['contains'],
        // Reject wildcard and sub-trigram terms before the source-backed lookup so
        // a broad candidate cohort cannot be paired with unselective matching.
        textSearchPolicy: 'literal_trigram',
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.page.final_url',
        displayName: 'Website final URL',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.page_load_time_tti_ms',
        displayName: 'Website page-load time (TTI, ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.time_to_first_byte_ms',
        displayName: 'Website time to first byte (ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.first_contentful_paint_ms',
        displayName: 'Website first contentful paint (ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.largest_contentful_paint_ms',
        displayName: 'Website largest contentful paint (ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.cumulative_layout_shift',
        displayName: 'Website cumulative layout shift',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        // The crawler computes the standards session-window maximum and keeps the
        // value sparse when the browser does not expose layout-shift entries.
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.dom_interactive_ms',
        displayName: 'Website DOM interactive time (ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.time_to_interactive_ms',
        displayName: 'Website time to interactive (ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        // The active crawler currently records DOM Interactive, not the
        // quiet-period semantics required by standard Time to Interactive.
        availability: 'planned',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.interaction_to_next_paint_ms',
        displayName: 'Website interaction to next paint (ms)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        // The crawler records the p98 interaction duration in its bounded Event
        // Timing window; domains without interactions remain unavailable.
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.document_size_bytes',
        displayName: 'Website document size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.script_size_bytes',
        displayName: 'Website script size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.stylesheet_size_bytes',
        displayName: 'Website stylesheet size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.image_size_bytes',
        displayName: 'Website image size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.media_size_bytes',
        displayName: 'Website media size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.font_size_bytes',
        displayName: 'Website font size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.xhr_size_bytes',
        displayName: 'Website XHR size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.ping_size_bytes',
        displayName: 'Website ping size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.other_resource_size_bytes',
        displayName: 'Website other resource size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.performance.fetch_size_bytes',
        displayName: 'Website fetch size (bytes)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        // The crawler now records this as a sparse fact, but existing rows were
        // written before fetch-size capture existed. Keep the metric planned until
        // the legacy values are cleared and the bounded backfill has completed.
        availability: 'planned',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.page.cookie_consent_detected',
        displayName: 'Website cookie consent detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.platform.current',
        displayName: 'Website platform',
        valueType: 'string',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'in', 'notIn'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.ecommerce.is_ecommerce',
        displayName: 'Website is e-commerce',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.analytics.google_tag_manager_detected',
        displayName: 'Google Tag Manager detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    ...realtimeDomainAnalyticsMetricCatalog,
    ...realtimeDomainMetadataMetricCatalog,
    {
        key: 'web.marketing.hubspot_detected',
        displayName: 'HubSpot detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.marketing.salesforce_detected',
        displayName: 'Salesforce detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.marketing.klaviyo_detected',
        displayName: 'Klaviyo detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.experience.hotjar_detected',
        displayName: 'Hotjar detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.experimentation.optimizely_detected',
        displayName: 'Optimizely detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.technology.wordpress_detected',
        displayName: 'WordPress detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.technology.shopify_detected',
        displayName: 'Shopify detected',
        valueType: 'boolean',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    ...realtimeDomainToolMetricCatalog,
    {
        key: 'web.accessibility.violation_score',
        displayName: 'Website accessibility violation score',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.accessibility.violations',
        displayName: 'Website accessibility violations',
        valueType: 'string_array',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['contains'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.accessibility.image_alt_ratio',
        displayName: 'Website image alt-text ratio',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.accessibility.image_count',
        displayName: 'Website image count',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.accessibility.image_alt_count',
        displayName: 'Website image alt-text count',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.accessibility.image_missing_alt_count',
        displayName: 'Website images missing alt text',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.accessibility.image_title_count',
        displayName: 'Website image title count',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'web.privacy.trackers_before_consent_count',
        displayName: 'Trackers loaded before cookie consent',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: domainCrawlEvidencePolicy,
    },
    {
        key: 'workforce.employees.current',
        displayName: 'Employees',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        workforceFactPolicy: exports.workforceEmployeesCurrentFactPolicy,
        evidencePolicy: workforceEvidencePolicy,
    },
    {
        key: 'signal.buzz.article_count.within_14d_mapped',
        displayName: 'Mapped news articles, last 14 days',
        // No mapped articles is unknown coverage, so this count never fabricates an observed zero.
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: new RealtimeMetricEvidencePolicy({
            sourceSystems: ['buzz'],
            temporalSemantics: 'derived',
            requiresObservedAt: true,
            requiresSourceRecordId: true,
        }),
    },
    ...realtimeBuzzEventMetricCatalog,
    ...realtimePrvTrademarkMetricCatalog,
    ...realtimeApolloOrganizationMetricCatalog,
    ...realtimeApolloHeadcountCurrentMetricCatalog,
    ...realtimeApolloHeadcountHistoricalMetricCatalog,
    ...realtimeApolloHeadcountGrowthMetricCatalog,
    ...realtimeLinkedInCurrentMetricCatalog,
    ...realtimeLinkedInMappingVerificationMetricCatalog,
    ...realtimeLinkedInEmployeeGrowthMetricCatalog,
    ...realtimeGooglePlacesCurrentMetricCatalog,
    ...realtimeGroupMetricCatalog,
    ...realtimeGroupEventSignalMetricCatalog,
    {
        key: 'signal.changed_ceo.within_14d_mapped',
        displayName: 'CEO change in the last 14 days (legacy parity)',
        valueType: 'boolean',
        queryPhases: ['scoring'],
        supportedOperators: ['eq'],
        availability: 'authorable',
        evidencePolicy: buzzSignalEvidencePolicy,
    },
    {
        key: 'workforce.employees.growth_1y',
        displayName: 'Employee growth, 1 year',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: workforceDerivedEvidencePolicy,
    },
    {
        key: 'company.workplaces.active_count',
        displayName: 'Active workplaces',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: workplaceEvidencePolicy,
        // Null until the SCB workplace sync has succeeded for the company, which is
        // deliberate: it separates "synced, has no active workplace" from "never
        // synced". Most of the population is still in the latter state.
        coverage: 'sparse',
    },
    {
        key: 'hiring.active_jobposts.current',
        displayName: 'Active job postings',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        evidencePolicy: jobtechActiveAdsEvidencePolicy,
        // Jobtech has a complete active-ad projection only for companies it can
        // match back to an organisation number.
        coverage: 'sparse',
    },
    {
        key: 'workforce.employees.per_active_workplace',
        displayName: 'Employees per active workplace',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: workplaceDerivedEvidencePolicy,
    },
    {
        key: 'hiring.active_jobposts.per_employee',
        displayName: 'Active job postings per employee',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: jobtechActiveAdsDerivedEvidencePolicy,
    },
    {
        key: 'hiring.careers.open_ads.current',
        displayName: 'Career site — open ads',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: careerHiringEvidencePolicy,
    },
    ...['previous', '1m', '3m', '6m', '1y'].flatMap((period) => [
        {
            key: `hiring.careers.open_ads.delta_count.${period}`,
            displayName: `Career site — open ads: count change (${careerHiringPeriodLabel[period]})`,
            valueType: 'number',
            queryPhases: ['eligibility', 'scoring'],
            supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
            availability: 'authorable',
            coverage: 'sparse',
            evidencePolicy: careerHiringDerivedEvidencePolicy,
        },
        {
            key: `hiring.careers.open_ads.delta_percent.${period}`,
            displayName: `Career site — open ads: percent change (${careerHiringPeriodLabel[period]})`,
            valueType: 'number',
            queryPhases: ['eligibility', 'scoring'],
            supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
            availability: 'authorable',
            coverage: 'sparse',
            evidencePolicy: careerHiringDerivedEvidencePolicy,
        },
    ]),
    {
        key: 'hiring.careers.group_open_ads.current',
        displayName: 'Group-level career site — observed open ads (not employee headcount)',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        coverage: 'sparse',
        evidencePolicy: careerHiringEvidencePolicy,
    },
    ...['previous', '1m', '3m', '6m', '1y'].flatMap((period) => [
        {
            key: `hiring.careers.group_open_ads.delta_count.${period}`,
            displayName: `Group-level career site — observed ad count change (${careerHiringPeriodLabel[period]})`,
            valueType: 'number',
            queryPhases: ['eligibility', 'scoring'],
            supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
            availability: 'authorable',
            coverage: 'sparse',
            evidencePolicy: careerHiringDerivedEvidencePolicy,
        },
        {
            key: `hiring.careers.group_open_ads.delta_percent.${period}`,
            displayName: `Group-level career site — observed ad percent change (${careerHiringPeriodLabel[period]})`,
            valueType: 'number',
            queryPhases: ['eligibility', 'scoring'],
            supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
            availability: 'authorable',
            coverage: 'sparse',
            evidencePolicy: careerHiringDerivedEvidencePolicy,
        },
    ]),
    {
        key: 'crm.workspace.current_m2',
        displayName: "Workspace area (m², client's latest open deal)",
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        // Pipedrive/manual office facts are intentionally excluded from the
        // source-backed migration scope. Keep the compiler mapping for legacy
        // reads, but do not expose the metric to new definitions.
        availability: 'deferred',
        dataScope: 'client',
        evidencePolicy: crmWorkspaceEvidencePolicy,
        // Only companies the client already has an open Pipedrive deal for.
        coverage: 'sparse',
    },
    financialReportingMetadataMetric('financial.reporting.fiscal_year.current', 'Current report fiscal year', 'number'),
    financialReportingMetadataMetric('financial.reporting.currency.current', 'Current report currency', 'string'),
    financialReportingMetadataMetric('financial.reporting.fiscal_year_length.current', 'Current fiscal year length (months)', 'number'),
    financialReportingMetadataMetric('financial.reporting.fiscal_year_end_month.current', 'Current fiscal year end month', 'number'),
    financialReportingMetadataMetric('financial.reporting.employees.current', 'Annual report employees', 'number'),
    financialReportingMetadataMetric('financial.reporting.updated_date.current', 'Current financials updated date', 'date'),
    {
        key: 'financial.revenue.current',
        displayName: 'Revenue',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.ebit.current',
        displayName: 'EBIT',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.ebitda.current',
        displayName: 'EBITDA',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.net_profit.current',
        displayName: 'Net profit',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.cost_of_sales.current',
        displayName: 'Cost of sales',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.gross_profit.current',
        displayName: 'Gross profit',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.personnel_expenses.current',
        displayName: 'Personnel expenses',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.pre_tax_profit.current',
        displayName: 'Pre-tax profit',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.cash_equivalents.current',
        displayName: 'Cash equivalents',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.accounts_receivable.current',
        displayName: 'Accounts receivable',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.inventories.current',
        displayName: 'Inventories',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.total_assets.current',
        displayName: 'Total assets',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.total_equity.current',
        displayName: 'Total equity',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    {
        key: 'financial.total_current_liabilities.current',
        displayName: 'Total current liabilities',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: financialStatementEvidencePolicy,
    },
    financialStatementMetric('financial.administrative_expenses.current', 'Administrative expenses'),
    financialStatementMetric('financial.selling_expenses.current', 'Selling expenses'),
    financialStatementMetric('financial.other_external_expenses.current', 'Other external expenses'),
    financialStatementMetric('financial.ebita.current', 'EBITA'),
    financialStatementMetric('financial.ebitd.current', 'EBITD'),
    financialStatementMetric('financial.depreciation_and_amortisation.current', 'Depreciation and amortisation'),
    financialStatementMetric('financial.depreciation.current', 'Depreciation'),
    financialStatementMetric('financial.amortisation.current', 'Amortisation'),
    financialStatementMetric('financial.net_financial_income_or_expenses.current', 'Net financial income or expenses'),
    financialStatementMetric('financial.financial_income.current', 'Financial income'),
    financialStatementMetric('financial.financial_expenses.current', 'Financial expenses'),
    financialStatementMetric('financial.other_current_operating_assets.current', 'Other current operating assets'),
    financialStatementMetric('financial.current_financial_assets.current', 'Current financial assets'),
    financialStatementMetric('financial.total_current_assets.current', 'Total current assets'),
    financialStatementMetric('financial.property_plant_and_equipment.current', 'Property, plant and equipment'),
    financialStatementMetric('financial.goodwill_and_intangible_assets.current', 'Goodwill and intangible assets'),
    financialStatementMetric('financial.non_current_financial_receivables.current', 'Non-current financial receivables'),
    financialStatementMetric('financial.other_non_current_operating_assets.current', 'Other non-current operating assets'),
    financialStatementMetric('financial.total_non_current_assets.current', 'Total non-current assets'),
    financialStatementMetric('financial.accounts_payable.current', 'Accounts payable'),
    financialStatementMetric('financial.current_financial_liabilities.current', 'Current financial liabilities'),
    financialStatementMetric('financial.post_employee_liabilities.current', 'Post-employment liabilities'),
    financialStatementMetric('financial.other_current_operating_liabilities.current', 'Other current operating liabilities'),
    financialStatementMetric('financial.other_non_current_operating_liabilities.current', 'Other non-current operating liabilities'),
    financialStatementMetric('financial.non_current_financial_liabilities.current', 'Non-current financial liabilities'),
    financialStatementMetric('financial.total_non_current_liabilities.current', 'Total non-current liabilities'),
    financialStatementMetric('financial.shareholder_equity.current', 'Shareholder equity'),
    financialStatementMetric('financial.retained_earnings.current', 'Retained earnings'),
    financialStatementMetric('financial.minority_interest.current', 'Minority interest'),
    financialStatementMetric('financial.dividends.current', 'Dividends'),
    financialStatementMetric('financial.net_change_in_cash.current', 'Net change in cash'),
    financialStatementMetric('financial.cash_flow_before_financial_activities.current', 'Cash flow before financial activities'),
    financialStatementMetric('financial.cash_flow_operating.current', 'Cash flow from operating activities'),
    financialStatementMetric('financial.net_capex.current', 'Net capital expenditure'),
    financialStatementMetric('financial.net_debt.current', 'Net debt'),
    financialStatementMetric('financial.market_cap.current', 'Market capitalisation'),
    financialStatementMetric('financial.enterprise_value.current', 'Enterprise value'),
    financialDerivedMetric('financial.gross_profit_margin.current', 'Gross profit margin (%)'),
    financialDerivedMetric('financial.ebit_margin.current', 'EBIT margin (%)'),
    financialDerivedMetric('financial.ebitda_margin.current', 'EBITDA margin (%)'),
    financialDerivedMetric('financial.pre_tax_profit_margin.current', 'Pre-tax profit margin (ratio)'),
    financialDerivedMetric('financial.net_profit_margin.current', 'Net profit margin (ratio)'),
    financialDerivedMetric('financial.equity_ratio.current', 'Equity ratio (%)'),
    financialDerivedMetric('financial.current_ratio.current', 'Current ratio'),
    financialDerivedMetric('financial.net_debt_to_ebit.current', 'Net debt / EBIT'),
    financialDerivedMetric('financial.net_debt_to_ebitda.current', 'Net debt / EBITDA'),
    financialDerivedMetric('financial.net_debt_to_equity.current', 'Net debt / equity'),
    financialDerivedMetric('financial.enterprise_value_to_ebit.current', 'Enterprise value / EBIT'),
    financialDerivedMetric('financial.enterprise_value_to_ebitda.current', 'Enterprise value / EBITDA'),
    financialDerivedMetric('financial.enterprise_value_to_revenue.current', 'Enterprise value / revenue'),
    {
        key: 'financial.revenue.per_employee',
        displayName: 'Revenue per employee',
        valueType: 'money',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: revenueDerivedEvidencePolicy,
    },
    {
        key: 'financial.revenue.growth_1y',
        displayName: 'Revenue growth, 1 year',
        valueType: 'number',
        queryPhases: ['eligibility', 'scoring'],
        supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
        availability: 'authorable',
        currencyPolicy: 'same_currency_only',
        evidencePolicy: revenueDerivedEvidencePolicy,
    },
];
/** Finds a catalog entry without exposing a mutable collection. */
const findRealtimeMetric = (key) => exports.realtimeMetricCatalog.find((metric) => metric.key === key);
exports.findRealtimeMetric = findRealtimeMetric;
/**
 * Central publication gate for a raw catalog metric. Declaring a metric as
 * authorable is insufficient by itself: every selectable fact must promise a
 * source observation and an immutable source-record identifier.
 */
const isAuthorableRealtimeMetric = (metric) => metric.availability === 'authorable' && metric.evidencePolicy !== undefined;
exports.isAuthorableRealtimeMetric = isAuthorableRealtimeMetric;
/**
 * Whether the metric's source reaches only part of the company population.
 *
 * An unknown key is not sparse: it is either a metric the compiler will reject
 * outright or one added after this catalog was written, and neither should be
 * reported to an author as a coverage problem.
 */
const isSparseCoverageMetric = (key) => (0, exports.findRealtimeMetric)(key)?.coverage === 'sparse';
exports.isSparseCoverageMetric = isSparseCoverageMetric;
/**
 * Maps a string predicate literal onto the casing actually stored in the source
 * column.
 *
 * `company.location.city` matches case-insensitively through ILIKE, so an author
 * reasonably expects `company.operational_status eq 'active'` to behave the same
 * way. It did not: the status column is compared with `=`, so the query returned
 * zero rows and reported no reason at all.
 *
 * Normalising in SQL with `LOWER()` would fix the comparison but make
 * `company_master_realtime_operational_status_idx` — a plain btree on the raw
 * column — unusable. Canonicalising the literal instead keeps the index and
 * still removes the silent empty result.
 *
 * A value that matches nothing is returned untouched, so a vendor value added
 * after this catalog was written still resolves. An ambiguous value (several
 * observed values differing only in case) is also returned untouched rather than
 * having one arbitrary spelling picked for it.
 */
const canonicalizeMetricStringValue = (metricKey, value) => {
    const observed = (0, exports.findRealtimeMetric)(metricKey)?.observedValues;
    if (observed === undefined || observed.includes(value))
        return value;
    const matches = observed.filter((candidate) => candidate.toLowerCase() === value.toLowerCase());
    return matches.length === 1 ? (matches[0] ?? value) : value;
};
exports.canonicalizeMetricStringValue = canonicalizeMetricStringValue;
//# sourceMappingURL=metric-catalog.js.map